/**
 * 🎙️ ROOMS TAB - Salons VOCAUX avec LiveKit
 * 
 * Architecture modulaire pour les salons vocaux temps réel
 * Utilise VoiceRoomProvider pour la connexion audio
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, MessageCircle, Mic, MicOff, Volume2, VolumeX,
  Phone, PhoneOff, Radio, Headphones, ArrowLeft, Send,
  Loader2, AlertCircle, Settings
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import { VoiceRoomProvider, useVoiceRoom, VoiceRoomsList, VoiceRoomActive } from '../../voice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// ROOM CARD COMPONENT
// ============================================

const RoomCard = ({ room, onJoin, isJoining }) => {
  const themeColors = {
    party: 'from-pink-600 to-orange-500',
    positivity: 'from-green-500 to-cyan-500',
    debate: 'from-red-500 to-orange-600',
    confessions: 'from-purple-600 to-indigo-600',
    love: 'from-pink-500 to-rose-500',
    gaming: 'from-blue-600 to-purple-600',
    default: 'from-gray-600 to-gray-700'
  };

  const bgColor = themeColors[room.theme] || themeColors.default;

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`relative overflow-hidden rounded-2xl cursor-pointer`}
      onClick={() => !isJoining && onJoin(room)}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${bgColor} opacity-80`} />
      <div className="absolute inset-0 bg-black/20" />
      
      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{room.emoji || '🎙️'}</span>
            <div>
              <h3 className="font-bold text-white text-lg">{room.name}</h3>
              <p className="text-white/70 text-sm">{room.description}</p>
            </div>
          </div>
        </div>

        {/* Live Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 px-3 py-1 bg-black/30 rounded-full backdrop-blur-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <Radio className="w-4 h-4 text-green-400" />
              <span className="text-sm text-white font-medium">LIVE</span>
            </div>
            <div className="flex items-center gap-1 px-3 py-1 bg-black/30 rounded-full backdrop-blur-sm">
              <Users className="w-4 h-4 text-white/70" />
              <span className="text-sm text-white">{room.participants || 0}</span>
            </div>
          </div>

          {/* Avatars preview */}
          {room.participantsList?.length > 0 && (
            <div className="flex -space-x-2">
              {room.participantsList.slice(0, 4).map((p, i) => (
                <div 
                  key={i}
                  className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/50 flex items-center justify-center text-xs font-bold text-white backdrop-blur-sm"
                >
                  {(p.userName || '?')[0]}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Join Button */}
        <Button 
          className="w-full mt-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/20 text-white"
          disabled={isJoining}
        >
          {isJoining ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Phone className="w-4 h-4 mr-2" />
          )}
          Rejoindre le salon
        </Button>
      </div>
    </motion.div>
  );
};

// ============================================
// ACTIVE ROOM VIEW
// ============================================

const ActiveRoomView = ({ onLeave }) => {
  const { 
    currentRoom, 
    participants, 
    isMuted, 
    isSpeaking,
    toggleMute, 
    leaveRoom,
    isConnected 
  } = useVoiceRoom();

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');

  const handleLeave = async () => {
    await leaveRoom();
    onLeave?.();
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    setMessages(prev => [...prev, {
      id: Date.now(),
      text: newMessage,
      user: 'Moi',
      time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    }]);
    setNewMessage('');
  };

  if (!currentRoom) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full flex flex-col bg-gradient-to-b from-gray-900 to-purple-900/50"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLeave}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="font-bold text-white text-lg">{currentRoom.name}</h2>
              <p className="text-sm text-green-400 flex items-center gap-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                En direct • {participants.length} participant{participants.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Participants */}
      <div className="flex-1 p-4 overflow-y-auto">
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
          {participants.map((p) => (
            <motion.div
              key={p.id}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="flex flex-col items-center gap-2"
            >
              <motion.div
                animate={p.isSpeaking ? {
                  scale: [1, 1.1, 1],
                  boxShadow: ['0 0 0 0 rgba(34, 197, 94, 0)', '0 0 0 15px rgba(34, 197, 94, 0.3)', '0 0 0 0 rgba(34, 197, 94, 0)']
                } : {}}
                transition={{ duration: 0.6, repeat: p.isSpeaking ? Infinity : 0 }}
                className={`w-16 h-16 rounded-full flex items-center justify-center font-bold text-xl relative
                  ${p.isSpeaking ? 'bg-green-500 ring-4 ring-green-400/50' : 
                    p.isMuted ? 'bg-gray-600' : 'bg-gradient-to-br from-purple-500 to-pink-500'}
                  text-white`}
              >
                {(p.name || '?')[0].toUpperCase()}
                
                {/* Mic status */}
                <div className={`absolute -bottom-1 -right-1 p-1.5 rounded-full ${
                  p.isMuted ? 'bg-red-500' : 'bg-green-500'
                }`}>
                  {p.isMuted ? (
                    <MicOff className="w-3 h-3 text-white" />
                  ) : (
                    <Mic className="w-3 h-3 text-white" />
                  )}
                </div>

                {/* Local badge */}
                {p.isLocal && (
                  <div className="absolute -top-1 -left-1 px-2 py-0.5 bg-cyan-500 rounded text-[10px] font-bold">
                    MOI
                  </div>
                )}
              </motion.div>
              <span className={`text-xs truncate max-w-full text-center ${
                p.isLocal ? 'text-cyan-400 font-bold' : 'text-gray-300'
              }`}>
                {p.name || 'Anonyme'}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Speaking indicator */}
        {isSpeaking && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6 p-4 bg-green-500/20 rounded-xl text-center border border-green-500/30"
          >
            <span className="text-green-400 font-bold text-lg">🎤 Tu parles en ce moment...</span>
          </motion.div>
        )}
      </div>

      {/* Chat messages (simplified) */}
      <div className="px-4 py-2 max-h-32 overflow-y-auto bg-black/20">
        {messages.map((msg) => (
          <div key={msg.id} className="text-sm py-1">
            <span className="text-cyan-400 font-medium">{msg.user}</span>
            <span className="text-gray-500 text-xs ml-2">{msg.time}</span>
            <p className="text-white/80">{msg.text}</p>
          </div>
        ))}
      </div>

      {/* Bottom controls */}
      <div className="p-4 border-t border-white/10 bg-black/30">
        {/* Chat input */}
        <div className="flex gap-2 mb-4">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Envoie un message..."
            className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
          />
          <Button onClick={sendMessage} className="bg-cyan-500 hover:bg-cyan-600">
            <Send className="w-4 h-4" />
          </Button>
        </div>

        {/* Voice controls */}
        <div className="flex justify-center gap-4">
          <Button
            onClick={toggleMute}
            size="lg"
            className={`rounded-full w-16 h-16 ${
              isMuted 
                ? 'bg-red-500 hover:bg-red-600' 
                : 'bg-green-500 hover:bg-green-600'
            }`}
          >
            {isMuted ? (
              <MicOff className="w-6 h-6" />
            ) : (
              <Mic className="w-6 h-6" />
            )}
          </Button>

          <Button
            onClick={handleLeave}
            size="lg"
            className="rounded-full w-16 h-16 bg-red-600 hover:bg-red-700"
          >
            <PhoneOff className="w-6 h-6" />
          </Button>
        </div>

        <p className="text-center text-xs text-gray-500 mt-3">
          {isMuted ? '🔇 Micro coupé - Clique pour parler' : '🎤 Les autres t\'entendent !'}
        </p>
      </div>
    </motion.div>
  );
};

// ============================================
// NOT CONFIGURED WARNING
// ============================================

const NotConfiguredWarning = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="p-6 rounded-2xl bg-yellow-500/10 border border-yellow-500/30 text-center"
  >
    <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
    <h3 className="text-white font-bold text-lg mb-2">Service vocal non configuré</h3>
    <p className="text-gray-400 text-sm mb-4">
      Les salons vocaux nécessitent une configuration LiveKit.
    </p>
    <div className="text-left bg-black/30 p-4 rounded-xl text-sm">
      <p className="text-gray-300 mb-2">Pour activer les salons vocaux :</p>
      <ol className="text-gray-400 space-y-1 list-decimal list-inside">
        <li>Créer un compte sur <a href="https://cloud.livekit.io" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">cloud.livekit.io</a></li>
        <li>Copier les clés API</li>
        <li>Ajouter dans le fichier .env :</li>
      </ol>
      <pre className="mt-2 p-2 bg-black/50 rounded text-xs text-green-400 overflow-x-auto">
{`LIVEKIT_URL=wss://your-app.livekit.cloud
LIVEKIT_API_KEY=your_api_key
LIVEKIT_API_SECRET=your_api_secret`}
      </pre>
    </div>
  </motion.div>
);

// ============================================
// MAIN ROOMS TAB COMPONENT
// ============================================

const RoomsTabContent = ({ customerId, customerName, isDarkMode }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConfigured, setIsConfigured] = useState(false);
  const { joinRoom, isConnecting, isConnected, currentRoom, serverUrl } = useVoiceRoom();

  // Check configuration
  useEffect(() => {
    setIsConfigured(!!serverUrl);
  }, [serverUrl]);

  // Load rooms
  useEffect(() => {
    const loadRooms = async () => {
      try {
        const res = await fetch(`${API_URL}/api/voice/rooms`);
        const data = await res.json();
        if (data.success) {
          setRooms(data.rooms);
        }
      } catch (err) {
        console.error('Erreur chargement salons:', err);
      } finally {
        setLoading(false);
      }
    };

    loadRooms();
    const interval = setInterval(loadRooms, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleJoinRoom = async (room) => {
    await joinRoom(room.id, room.name);
  };

  // If connected, show active room
  if (isConnected && currentRoom) {
    return <ActiveRoomView onLeave={() => {}} />;
  }

  return (
    <div className="p-4 space-y-6 bg-gradient-to-b from-gray-900 to-purple-900/50">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="w-6 h-6 text-purple-400" />
          <h2 className="font-bold text-white text-xl">Salons Vocaux</h2>
        </div>
        <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-sm text-green-400">{rooms.reduce((a, r) => a + (r.participants || 0), 0)} en ligne</span>
        </div>
      </div>

      {/* Warning if not configured */}
      {!isConfigured && !loading && <NotConfiguredWarning />}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
      )}

      {/* Rooms grid - avec overflow contrôlé */}
      {!loading && isConfigured && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
          {rooms.map((room) => (
            <RoomCard
              key={room.id}
              room={room}
              onJoin={handleJoinRoom}
              isJoining={isConnecting}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && rooms.length === 0 && isConfigured && (
        <div className="text-center py-12 text-gray-400">
          <Radio className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">Aucun salon disponible</p>
          <p className="text-sm">Les salons seront bientôt disponibles !</p>
        </div>
      )}
    </div>
  );
};

// ============================================
// WRAPPER WITH PROVIDER
// ============================================

export const RoomsTab = ({ customerId, customerName, isDarkMode }) => {
  return (
    <VoiceRoomProvider userId={customerId} userName={customerName}>
      <RoomsTabContent 
        customerId={customerId} 
        customerName={customerName} 
        isDarkMode={isDarkMode} 
      />
    </VoiceRoomProvider>
  );
};

export default RoomsTab;
